import { Injectable } from "@angular/core";
import { Area, Assertion, Component, db } from "../../core/database/db";

export type ChecklistComponentEntry = {
  component: Component;
  assertions: Assertion[];
}

export type ChecklistAreaGroup = {
  area: Area;
  components: ChecklistComponentEntry[];
}

@Injectable({
  providedIn: "root"
})
export class ChecklistService {

  /**
   * Returns true when the BQ8 checklist structure (zones/areas/components) for a lot
   * has been materialized and stored locally. A job can be pulled into IndexedDB before
   * the server materializes its structure, which leaves the device with an empty checklist.
   */
  async hasChecklist(lotId: number): Promise<boolean> {
    const zones = await db.zone.where("lotId").equals(lotId)
      .toArray();
    if (zones.length === 0) {
      return false;
    }

    const bq8Areas = await db.area.where("zoneId").anyOf(zones.map(z => z.id))
      .filter(a => a.buildQualityStepScope === 8)
      .toArray();

    const areaIds = bq8Areas.map(a => a.id).filter((id): id is number => id !== undefined);
    if (areaIds.length === 0) {
      return false;
    }

    const componentCount = await db.component.where("areaId").anyOf(areaIds)
      .count();

    return componentCount > 0;
  }

  async getChecklist(lotId: number): Promise<ChecklistAreaGroup[]> {
    const zones = await db.zone.where("lotId").equals(lotId)
      .toArray();
    const areas = await db.area.where("zoneId").anyOf(zones.map(z => z.id))
      .toArray();
    const bq8Areas = areas.filter(a => a.buildQualityStepScope === 8);
    if (bq8Areas.length === 0) {
      return [];
    }

    const areaIds = bq8Areas.map(a => a.id).filter((id): id is number => id !== undefined);
    const components = await db.component.where("areaId").anyOf(areaIds)
      .toArray();

    const componentIds = components.map(c => c.id).filter((id): id is number => id !== undefined);
    const assertions = await db.assertion.where("componentId").anyOf(componentIds)
      .toArray();

    return bq8Areas
      .map(area => ({
        area,
        components: components
          .filter(c => c.areaId === area.id)
          .map(component => ({
            component,
            assertions: assertions.filter(a => a.componentId === component.id)
          }))
          .filter(entry => entry.assertions.length > 0)
          .sort((a, b) => a.component.name.localeCompare(b.component.name))
      }))
      .filter(group => group.components.length > 0);
  }
}
